import { useMutation, useQuery } from "@tanstack/react-query";
import { useAxios } from "../useAxios";
import { USER_API } from "config";
import { projectKeys } from "queries/project.keys";
import { handleToast } from "utils/toasts";
import { getApiErrorToastMessage } from "utils/errorHandling";
import { useQueryClient } from "@tanstack/react-query";

export interface UpgradeEligibilityResponse {
  canUpgrade: boolean;
  availableLicenses: number;
  message?: string;
}

/**
 * Hook for upgrading trial projects to purchased projects
 */
export const useUpgradeProject = (projectId?: string | number) => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  // Query to check upgrade eligibility
  const upgradeEligibility = useQuery<UpgradeEligibilityResponse>({
    queryKey: projectKeys.upgradeEligibility(projectId!),
    queryFn: async () => {
      const response = await axios.get(`${USER_API.projects}/${projectId}/upgrade-eligibility`);
      return response.data;
    },
    enabled: !!projectId,
  });

  // Mutation to upgrade the project
  const upgradeProject = useMutation({
    mutationFn: async (projectId: string | number) => {
      const response = await axios.post(`${USER_API.projects}/${projectId}/upgrade`);
      return response.data;
    },
    onSuccess: (_, projectId) => {
      // Invalidate all project-related queries
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.upgradeEligibility(projectId) });

      handleToast("Project upgraded successfully!", "success");

      window.location.reload();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to upgrade project. Please try again.";
      handleToast(message, "error");
    },
  });

  // Reason the server gives for a denied or failed check; empty when it gives none.
  const eligibilityMessage = upgradeEligibility.isError
    ? getApiErrorToastMessage(upgradeEligibility.error, "")
    : (upgradeEligibility.data?.message ?? "");

  return {
    upgradeEligibility,
    upgradeProject,
    isUpgrading: upgradeProject.isPending,
    canUpgrade: upgradeEligibility.data?.canUpgrade ?? false,
    availableLicenses: upgradeEligibility.data?.availableLicenses ?? 0,
    isCheckingEligibility: upgradeEligibility.isInitialLoading,
    eligibilityMessage,
  };
};
